/**
 * ----- [ rc-slider - @see https://openbase.com/js/rc-slider ] ---------------
 *
 * See also https://github.com/react-component/slider/issues/825 as the doc has
 * not kept pace with the latest version. In particular there is no exported
 * member 'Range' - instead, use the boolean prop range on Slider itself to enable
 * range functionality.
 *
 * For tooltips, you can't use the HOC Slider.createSliderWithTooltip either;
 * @see https://codesandbox.io/s/lzsr20?file=/components/TooltipSlider.tsx which
 * shows a very clunky way to do it.
 */
// import classnames from 'classnames'
import { action, observable } from 'mobx';
import { observer } from 'mobx-react';
import Slider /*, { Range } */ from 'rc-slider';
// tslint:disable-next-line: no-submodule-imports
import { MarkObj } from 'rc-slider/lib/Marks';
import React /*, { CSSProperties }*/ from 'react';

import { Pair } from '../../../adl-gen/sys/types';

// tslint:disable-next-line: no-import-side-effect
import './rc-slider.css';

// /// Can't inject this into rc-slider props unfortunately, and the maintainers refuse to support CSS-Modules
// const styles = require('./rc-slider.css')
// ...so...
// export interface CustomCSS extends CSSProperties {
//   'class': string
// }

// MarkObj: { style, label }
// tslint:disable-next-line: no-null-undefined-union
export type SliderMarks = Record<string | number, React.ReactNode | MarkObj>;

interface SliderProps {
  initial?: number | Pair<number, number>;
  min: number;
  max: number;
  step?: number | null;
  marks: SliderMarks;
  value?: number;
  onChange?(value: number | number[]): void;
  debug?: boolean;
}

@observer
export class DemoSlider extends React.Component<SliderProps> {
  @observable value: number = 0.2;
  @observable values: Pair<number, number> = { v1: 0.2, v2: 0.8 };

  constructor(props: Readonly<SliderProps>) {
    super(props);
    if (props.initial) {
      this.value = props.initial as number;
    }
  }

  /// If using *range*, the value is an array, otherwise it's a scalar
  @action
  setValue = (value: number | number[]) => {
    const { onChange } = this.props;
    /* if you're only using the slider  */
    this.value = value as number;
    if (onChange) {
      onChange(value);
    }
  };
  @action
  setValues = (value: number | number[]) => {
    const { onChange } = this.props;
    // /* if you're using range */
    const input = value as number[];
    this.values = { v1: input[0], v2: input[1] };
    if (onChange) {
      onChange(value);
    }
  };

  render() {
    // const railStyle: CustomCSS = { class: 'railStyle' }
    return (
      <div>
        <Slider
          // Switching this ON => multiple handles and a range of values
          // range
          value={this.value}
          onChange={this.setValue}
          dots
          {...this.props}
          // railStyle={railStyle}
        />
        <div style={{ marginTop: 40 }}>
          <b>Selected Value: </b>
          {this.value}
        </div>
      </div>
    );
  }
}

/// ----- [ NO MobX REQUIRED ] ------------------------------------
/// This is basically a wrapper for the debug prop (and passes 'dots')
// @observer
export class StandardSlider extends React.Component<SliderProps> {
  // @observable value?: number

  // constructor(props: Readonly<SliderProps>) {
  //   super(props)
  //   if (props.initial) { this.value = props.initial as number }
  // }

  // /// If using *range*, the value is an array, otherwise it's a scalar
  // @action
  // setValue = (value: number | number[]) => {
  //   const { onChange } = this.props
  //   /* if you're only using the slider  */
  //   this.value = value as number
  //   if (onChange) { onChange(value) }
  // }

  render() {
    // const railStyle: CustomCSS = { class: 'railStyle' }
    return (
      <div>
        <Slider
          // Switching this ON => multiple handles and a range of values
          // range
          dots
          // this.props without the initial or debug
          // min={this.props.min}
          // max={this.props.max}
          // step={this.props.step}
          // marks={this.props.marks}
          // value={this.props.value}
          {...this.props}
          /// Set this after the rest of props or the local update gets lost
          // onChange={this.setValue}
          // railStyle={railStyle}
        />
        {this.props.debug && (
          <div style={{ marginTop: 40 }}>
            <b>Selected Value: </b>
            {this.props.value}
          </div>
        )}
      </div>
    );
  }
}

@observer
export class RangeSlider extends React.Component<SliderProps> {
  @observable values: Pair<number, number> = { v1: 0.2, v2: 0.8 };

  constructor(props: Readonly<SliderProps>) {
    super(props);
    if (props.initial) {
      this.values = props.initial as Pair<number, number>;
    }
  }

  /// If using *range*, the value is an array, otherwise it's a scalar
  @action
  setValues = (value: number | number[]) => {
    const { onChange } = this.props;
    // /* if you're using range */
    const input = value as number[];
    this.values = { v1: input[0], v2: input[1] };
    if (onChange) {
      onChange(value);
    }
  };

  render() {
    // const railStyle: CustomCSS = { class: 'railStyle' }
    return (
      <div>
        <Slider
          // Switching this ON => multiple handles and a range of values
          range
          value={[this.values.v1, this.values.v2]}
          dots
          {...this.props}
          /// Set this after the rest of props or the local update gets lost
          onChange={this.setValues}
          // railStyle={railStyle}
        />
        {this.props.debug && (
          <div style={{ marginTop: 40 }}>
            <b>Selected Values: </b>
            {`[${this.values.v1},${this.values.v2}]`}
          </div>
        )}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/ui/widgets/slider/slider.tsx